import * as Eff from "redux-saga/effects";

import { OPEN_CART_RESOURCE_ROOT, getResourceRoot } from "Utils/apiUtils";
import {
  recordPriceCalculationIfRecording,
  recordProductIfRecording,
} from "Utils/fixtureRecordingUtils";
import {
  getCampaignAndLeadQueryParams,
  getQueryString,
} from "Utils/navigation/queryParameterUtils";
import { createRequest, getError } from "Utils/requestUtils";
import { all, call, put, select } from "redux-saga/effects";
import {
  GetProductRequestParams,
  OldPriceCalculationRequestParams,
  OperationType,
  PriceCalculationRequestParams,
  PriceCalculationResponse,
  StoredAdviceCartStartPayload,
} from "./priceCalculationInterfaces";
import {
  clearPriceCalculationResults,
  editPriceCalculationRequest,
  getStoredAdviceCartStart,
  getStoredAdviceCartSucceeded,
  getStoredProductStart,
  getStoredProductSucceeded,
  operationFailed,
  sendPriceCalculationRequestStart,
  sendPriceCalculationRequestSucceeded,
  sendPriceCalculationRequestWithCompositesSucceeded,
  sendPriceCalculationRequestsFinished,
  sendPriceCalculationRequestsStart,
  storedProductOperationFailed,
  updatePriceCalculationFormStates,
} from "./priceCalculationSlice";
import { mapProductToForm, mapToPriceRequest } from "./productMappers/index";

import { PayloadAction } from "@reduxjs/toolkit";
import { getAgreementProduct } from "Components/Features/CheckPrice/helpers/getAgreementProduct";
import { getEditedAkaAdviceStart } from "Store/akaAdvice/slice";
import { Cart } from "Store/cart/cartInterfaces";
import { fetchCartUrl } from "Store/cart/cartSagas";
import { ErrorStateOld, INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProductPriceRequestInfo } from "Store/interfaces/products/checkPrice";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { ReduxState } from "Store/rootReducer";
import { nextStep } from "Store/steps/stepsSlice";
import { NewCustomerResponse } from "Store/userIdentification";
import { isDefined } from "Utils/functions";
import { deleteFormValuesFromLocalStorage } from "Utils/localStorage/localStorageUtils";
import { withPropertyNamePrefix } from "Utils/recordUtils";
import { AxiosResponse } from "axios";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* sendPriceCalculationRequest(
  payload: OldPriceCalculationRequestParams,
) {
  try {
    const oldPriceCalculationRequest = mapToPriceRequest(
      payload.priceCalculationRequest,
    );
    const params = getCampaignAndLeadQueryParams();
    const { data }: AxiosResponse<PriceCalculationResponse> = yield call(
      createRequest,
      {
        method: "post",
        requestBody: oldPriceCalculationRequest,
        url: payload.productPriceUrl,
        params: params,
      },
    );

    const urlWithParams = `${payload.productPriceUrl}${getQueryString(params)}`;
    recordPriceCalculationIfRecording(urlWithParams, data);

    if (payload.clearOldPriceCalculations) {
      yield put(clearPriceCalculationResults());
    }

    if (isDefined(data.composites)) {
      const mappedComposites = withPropertyNamePrefix(
        data.composites,
        payload.compositePrefix,
      );

      yield put(
        sendPriceCalculationRequestWithCompositesSucceeded({
          ...data.configurations,
          composites: mappedComposites,
        }),
      );
    } else {
      yield put(sendPriceCalculationRequestSucceeded(data.configurations));
    }

    if (payload.stepForwardOnSuccess) {
      yield put(nextStep());
    }
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.SEND_PRICE_CALCULATION_REQUEST,
        response: getError(e),
      }),
    );
  }
}

const statePriceCalculationProcessesError = (
  state: ReduxState,
): ErrorStateOld<OperationType> | null =>
  state.priceCalculation.priceCalculationResults.error;

export function* startPriceCalculationProcesses({
  payload,
}: PayloadAction<PriceCalculationRequestParams[]>): Generator<any, void, any> {
  const stepForwardOnSuccess = payload.some(
    ({ stepForwardOnSuccess }) =>
      isDefined(stepForwardOnSuccess) && stepForwardOnSuccess,
  );
  yield all(
    payload.map((params) =>
      call(startPriceCalculation, { ...params, stepForwardOnSuccess: false }),
    ),
  );

  const priceRequestError = yield select(statePriceCalculationProcessesError);

  if (stepForwardOnSuccess && priceRequestError === null) {
    yield put(nextStep());
  }
  yield put(sendPriceCalculationRequestsFinished());
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* startPriceCalculationProcess({
  payload,
}: PayloadAction<PriceCalculationRequestParams>) {
  yield call(startPriceCalculation, payload);
  yield put(sendPriceCalculationRequestsFinished());
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* startPriceCalculation(payload: PriceCalculationRequestParams) {
  if ("priceCalculationRequest" in payload) {
    yield call(sendPriceCalculationRequest, payload);
    return;
  }

  const {
    userIdentification: userState,
    priceCalculation,
    partner,
  }: ReduxState = yield select();

  const {
    agreement,
    requestInfo,
    productPriceUrl,
    clearOldPriceCalculations,
    stepForwardOnSuccess = true,
  } = payload;

  yield put(editPriceCalculationRequest(requestInfo));

  const userIdentification = userState.current;

  if (!isDefined(userIdentification)) {
    return;
  }

  const { discountResponse, newCartResponse, newCustomerResponse } =
    userIdentification;

  const requestPayload: PriceCalculationRequestParams = {
    priceCalculationRequest: {
      productInfo: {
        ...priceCalculation.priceCalculationRequest.productInfo,
        ...requestInfo,
        cartId: newCartResponse.cartId,
        dateOfBirth: findDateOfBirth(requestInfo, newCustomerResponse),
        customerRef: newCustomerResponse.customerRef,
      } as InsuranceProductPriceRequestInfo, //TODO: Fix typing here
      discountInfo: {
        agreementProduct: getAgreementProduct({
          agreementProductOverwrite: agreement,
          manualAgreementProduct: newCustomerResponse.manualAgreementProduct,
          partnerAgreement: partner.agreementNumber,
          agreementProductFromDiscount: discountResponse.f2100.agreementProduct,
        }),
        isNewInsuranceCustomer: discountResponse.f2100.isNewInsuranceCustomer,
        paymentPeriod: discountResponse.f2100.paymentPeriod,
      },
      numberOfProductsInCart: payload.numberOfProductsInCart,
    },
    stepForwardOnSuccess,
    productPriceUrl: productPriceUrl,
    clearOldPriceCalculations: clearOldPriceCalculations,
    compositePrefix: payload.compositePrefix,
  };

  yield call(sendPriceCalculationRequest, requestPayload);
}

const findDateOfBirth = (
  info: InsuranceProductPriceRequestInfo,
  customer: NewCustomerResponse,
): string | undefined =>
  "dateOfBirth" in info ? info.dateOfBirth : customer.dateOfBirth;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* getStoredProductRequest({
  payload,
}: PayloadAction<GetProductRequestParams>) {
  try {
    const resourceRoot =
      payload.type === INSURANCE_NAME_VALUE.HEALTH ||
      payload.type === INSURANCE_NAME_VALUE.AKA_HEALTH
        ? getResourceRoot()
        : OPEN_CART_RESOURCE_ROOT;

    const { data }: AxiosResponse<InsuranceProduct> = yield call(
      createRequest,
      {
        method: "get",
        open: resourceRoot === OPEN_CART_RESOURCE_ROOT,
        url: `${resourceRoot}/products/${payload.productId}`,
      },
    );
    recordProductIfRecording(data);

    deleteFormValuesFromLocalStorage();
    yield put(
      updatePriceCalculationFormStates(mapProductToForm(payload.type, data)),
    );
    yield put(getStoredProductSucceeded(data));
  } catch (e) {
    yield put(
      storedProductOperationFailed({
        operationType: OperationType.SEND_GET_PRODUCT_REQUEST,
        response: getError(e),
      }),
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* getStoredAdviceCartRequests({
  payload,
}: PayloadAction<StoredAdviceCartStartPayload>) {
  try {
    const { data }: AxiosResponse<Cart> = yield call(createRequest, {
      method: "get",
      url: fetchCartUrl(payload.cartId, true),
      open: false,
    });

    yield put(getStoredAdviceCartSucceeded(data));
    if (data.advice) {
      yield put(
        getEditedAkaAdviceStart({
          isCollectiveAdvice: payload.isCollectiveAdvice,
          cart: data,
        }),
      );
    }
  } catch (e) {
    yield put(
      storedProductOperationFailed({
        operationType: OperationType.SEND_GET_ADVICE_CART_REQUEST,
        response: getError(e),
      }),
    );
  }
}

export function* priceCalculationSaga(): Generator {
  yield takeLatest(
    sendPriceCalculationRequestStart,
    startPriceCalculationProcess,
  );
  yield takeLatest(
    sendPriceCalculationRequestsStart,
    startPriceCalculationProcesses,
  );
  yield takeLatest(getStoredProductStart, getStoredProductRequest);
  yield takeLatest(getStoredAdviceCartStart, getStoredAdviceCartRequests);
}

const takeLatest: any = Eff.takeLatest;
